import { Box, CssBaseline, LinearProgress, styled } from "@mui/material";
import { type FC, Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { ThemeProvider } from "../../../styles/theme/theme-provider";
import { type DisplayTheme, SettingsConsumer } from "../../../utils/contexts/settings-context";
import { SidebarProvider } from "../../../utils/contexts/sidebar-context";
import { AppToaster } from "./app-toaster";
import { CeAdminSidebar } from "./ce-admin-sidebar";
import { Sidebar, sidebarWidth } from "./sidebar/sidebar";
import { SidebarUiProvider, useSidebarUi } from "./sidebar/sidebar-ui-context";

const LayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: sidebarWidth,
  },
  paddingTop: 0,
}));

export const LightLayout: FC = () => <Layout themeOverride="light" />;

export const Layout: FC<{ themeOverride?: DisplayTheme }> = (props) => (
  <SidebarUiProvider>
    <LayoutInner {...props} />
  </SidebarUiProvider>
);

const LayoutInner: FC<{ themeOverride?: DisplayTheme }> = ({ themeOverride }) => {
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  const { pathname } = useLocation();
  const { closeSidebar } = useSidebarUi();
  useEffect(() => {
    closeSidebar();
  }, [closeSidebar, pathname]);
  return (
    <SettingsConsumer>
      {({ settings }) => (
        <ThemeProvider mode={themeOverride ?? settings.theme}>
          <SidebarProvider>
            <CssBaseline />
            <AppToaster />
            <LayoutRoot>
              <Box
                sx={{
                  display: "flex",
                  flex: "1 1 auto",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Suspense fallback={<LinearProgress />}>
                  <Outlet />
                </Suspense>
              </Box>
            </LayoutRoot>
            <Sidebar />
          </SidebarProvider>
        </ThemeProvider>
      )}
    </SettingsConsumer>
  );
};

export const CeAdminLayout: FC = () => (
  <SidebarUiProvider>
    <CeAdminLayoutInner />
  </SidebarUiProvider>
);

const CeAdminLayoutInner: FC = () => {
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  const { pathname } = useLocation();
  const { closeSidebar } = useSidebarUi();
  useEffect(() => {
    closeSidebar();
  }, [closeSidebar, pathname]);

  return (
    <ThemeProvider mode="ce-admin">
      <AppToaster />
      <LayoutRoot>
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Outlet />
        </Box>
      </LayoutRoot>

      <CeAdminSidebar />
    </ThemeProvider>
  );
};
